<template>
  <div>
    <!-- 제목 -->
    <v-sheet outlined class="d-flex align-center ma-2 px-4" height="60">
      <font class="font-weight-bold text-h6 grey--text text--darken-2">직원 관리</font>
    </v-sheet>

    <!-- 본문 -->
    <v-sheet outlined class="pa-2 ma-2">
      <div class="d-flex justify-end mb-2">
        <v-checkbox v-model="includeAdmins" class="shrink mt-0" label="관리자 포함" color="primary" hide-details dense></v-checkbox>
      </div>
      <!-- 목록 -->
      <v-radio-group v-model="selected" hide-details>
        <v-data-table
          hide-default-header
          :items="list"
          :footer-props="{
            'items-per-page-options': [10, 20, 100, -1],
            itemsPerPageText: '페이지당 직원 수',
          }"
          no-data-text=""
        >
          <template v-slot:header>
            <tr>
              <th style="width: 52px"></th>
              <th style="width: 100px">프로필</th>
              <th style="width: 100px">유형</th>
              <th style="width: 100px">이름</th>
              <th style="width: 150px">아이디</th>
              <th style="width: 100px">휴대폰번호</th>
              <th style="width: 150px">지점</th>
              <th style="width: 280px">스케줄</th>
              <th style="width: 250px">가능 컨셉</th>
              <th style="width: 200px">마지막 로그인 시간</th>
            </tr>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td>
                <v-radio class="ml-1" hide-details dense :value="item" @change="selectedItem = item"></v-radio>
              </td>
              <td class="text-center profile-cell">
                <v-avatar size="48" class="mx-auto">
                  <v-img :src="getProfileImageSrc(item.profile_image)">
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-1" size="20"></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                </v-avatar>
              </td>
              <td class="text-truncate">
                {{ item.type }}
              </td>
              <td class="text-truncate">
                {{ getStaffDisplayName(item) }}
              </td>
              <td class="text-truncate">
                {{ item.user_id }}
              </td>
              <td class="text-truncate">
                {{ item.phoneNumber }}
              </td>
              <td class="text-truncate">
                {{ item.branchName ? `${item.branchName} (${item.branchCode})` : "-" }}
              </td>
              <td class="schedule-cell">
                <template v-if="item.scheduleId">
                  <div class="d-flex flex-column">
                    <div v-for="day in weekDays" :key="`${item.id}-${day.key}`" v-if="getDaySchedule(item, day).length" class="schedule-line">
                      <span class="font-weight-bold">{{ day.shortLabel }}:</span>
                      <span class="ml-1 text-truncate">{{ getDaySchedule(item, day).join(", ") }}</span>
                    </div>
                    <div v-if="!hasScheduleSlots(item)" class="grey--text text--darken-1">등록된 시간이 없습니다.</div>
                    <v-btn icon small class="mt-1 align-self-start" @click.stop="openScheduleDialog(item)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </template>
                <template v-else>
                  <div class="d-flex align-center">
                    <span class="grey--text text--darken-1 mr-2">스케줄 없음</span>
                    <v-btn icon small color="primary" @click.stop="openScheduleDialog(item)">
                      <v-icon small>mdi-calendar-plus</v-icon>
                    </v-btn>
                  </div>
                </template>
              </td>
              <td class="concept-cell">
                <template v-if="hasConcepts(item)">
                  <div class="concept-chip-wrapper">
                    <v-chip small outlined v-for="concept in getConceptDisplayList(item)" :key="`${item.id}-concept-${concept.id}`" class="ma-1">
                      {{ concept.title }}
                    </v-chip>
                  </div>
                  <v-btn icon small class="mt-1 align-self-start" @click.stop="openConceptDialog(item)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <template v-else>
                  <div class="d-flex align-center">
                    <span class="grey--text text--darken-1 mr-2">컨셉 없음</span>
                    <v-btn icon small color="primary" @click.stop="openConceptDialog(item)">
                      <v-icon small>mdi-playlist-plus</v-icon>
                    </v-btn>
                  </div>
                </template>
              </td>
              <td class="text-truncate">
                {{ new Date(item.last_login).toLocaleString() }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-radio-group>

      <!-- 버튼 -->
      <v-sheet class="d-flex justify-end mt-2 pa-2" outlined>
        <v-btn class="px-6 ml-3" color="teal" large dark depressed @click="openChangePasswordDialog()">
          <v-icon small left>mdi-key</v-icon>
          비밀번호 변경
        </v-btn>
        <v-btn class="px-6 ml-3" color="orange" large dark depressed @click="dialog.update = true">
          <v-icon small left>mdi-circle-edit-outline</v-icon>
          수정
        </v-btn>
        <v-btn class="px-6 ml-3" color="blue" large dark depressed @click="createItem()">
          <v-icon small left>mdi-shape-circle-plus</v-icon>
          추가
        </v-btn>
        <v-btn class="px-6 ml-3" color="red" large dark depressed @click="deleteItem()">
          <v-icon small left>mdi-close-circle-outline</v-icon>
          삭제
        </v-btn>
      </v-sheet>
    </v-sheet>

    <!-- 비밀번호 변경 (dialog) -->
    <v-dialog v-model="dialog.changePassword" width="auto" content-class="rounded-xl">
      <ChangePassword :selectedItem="selectedItem" @close="dialog.changePassword = false" :key="componentChangePasswordKey" />
    </v-dialog>

    <!-- 수정 (dialog) -->
    <v-dialog v-model="dialog.update" width="auto" content-class="rounded-xl">
      <Update :selectedItem="selectedItem" @close="dialog.update = false" @updated="load" :key="componentKey" />
    </v-dialog>

    <!-- 추가 (dialog) -->
    <v-dialog v-model="dialog.insert" width="auto" content-class="rounded-xl">
      <Insert @close="dialog.insert = false" @inserted="load" :key="componentCreateKey" />
    </v-dialog>

    <!-- 스케줄 (dialog) -->
    <v-dialog v-model="dialog.schedule" max-width="900" content-class="rounded-xl">
      <v-card>
        <v-card-title class="font-weight-bold">
          작가 스케줄 설정
          <span class="ml-2 font-weight-regular grey--text text--darken-1">{{ scheduleForm.staffName || "-" }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div v-if="scheduleLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else>
            <div v-for="(day, index) in weekDays" :key="`schedule-${day.key}-${scheduleComponentKey}`" class="mb-4">
              <div class="font-weight-medium mb-2">{{ day.label }}</div>
              <WeekTimeComponent :key="`week-time-${day.key}-${scheduleComponentKey}`" :src="scheduleForm.operationSettings[index]" @change="(value) => setScheduleDay(index, value)" />
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeScheduleDialog">취소</v-btn>
          <v-btn color="primary" :loading="scheduleSaving" @click="saveSchedule">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 컨셉 (dialog) -->
    <v-dialog v-model="dialog.concept" max-width="1000" content-class="rounded-xl">
      <v-card>
        <v-card-title class="font-weight-bold">
          작가 컨셉 설정
          <span class="ml-2 font-weight-regular grey--text text--darken-1">{{ conceptForm.staffName || "-" }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-text-field
            v-model="conceptSearchKeyword"
            label="제목으로 검색"
            append-icon="mdi-magnify"
            clearable
            outlined
            dense
            :disabled="conceptOptionsLoading"
            @click:append="searchConceptOptions"
            @keyup.enter="searchConceptOptions"
            @click:clear="clearConceptSearch"
          ></v-text-field>

          <div class="mb-3" v-if="conceptForm.selectedConceptIds.length">
            <span class="font-weight-bold">선택된 컨셉:</span>
            <div class="concept-chip-wrapper mt-2">
              <v-chip v-for="concept in selectedConceptDisplayList" :key="`selected-${concept.id}`" small outlined class="ma-1" close @click:close="toggleConceptSelection(concept.id)">
                {{ concept.title }}
              </v-chip>
            </div>
          </div>

          <div v-if="conceptDialogLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else>
            <div v-if="conceptOptionsLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <template v-else>
              <v-row v-if="conceptOptions.length" dense>
                <v-col cols="12" sm="6" md="4" v-for="concept in conceptOptions" :key="concept.id">
                  <v-card outlined class="concept-card" :class="{ 'concept-card--selected': isConceptSelected(concept.id) }" @click="toggleConceptSelection(concept.id)">
                    <v-img :src="getConceptThumbnailSrc(concept.thumbnail_image)" height="140" class="concept-thumb" contain>
                      <template v-slot:placeholder>
                        <div class="d-flex fill-height align-center justify-center grey lighten-3">
                          <v-icon color="grey">mdi-image-off</v-icon>
                        </div>
                      </template>
                    </v-img>
                    <v-card-title class="text-truncate">{{ concept.title }}</v-card-title>
                    <v-card-subtitle class="grey--text text--darken-1">{{ concept.status }}</v-card-subtitle>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-icon small color="primary" v-if="isConceptSelected(concept.id)">mdi-check-circle</v-icon>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
              <div v-else class="text-center grey--text text--darken-1 py-6">검색된 컨셉이 없습니다.</div>
            </template>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeConceptDialog">취소</v-btn>
          <v-btn color="primary" :loading="conceptSaving" @click="saveConcepts">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Update from "./staff/Update"
import Insert from "./staff/Insert"
import ChangePassword from "./staff/ChangePassword"
import WeekTimeComponent from "@/views/admin/operation_time/WeekTimeComponent"

const WEEK_DAYS = [
  { key: "mon", label: "월요일", shortLabel: "월", field: "scheduleMon" },
  { key: "tue", label: "화요일", shortLabel: "화", field: "scheduleTue" },
  { key: "wed", label: "수요일", shortLabel: "수", field: "scheduleWed" },
  { key: "thu", label: "목요일", shortLabel: "목", field: "scheduleThu" },
  { key: "fri", label: "금요일", shortLabel: "금", field: "scheduleFri" },
  { key: "sat", label: "토요일", shortLabel: "토", field: "scheduleSat" },
  { key: "sun", label: "일요일", shortLabel: "일", field: "scheduleSun" },
]

const createEmptyOperationSettings = () => WEEK_DAYS.map(() => [])

const cloneOperationSettings = (settings = []) =>
  WEEK_DAYS.map((_, index) => {
    const values = Array.isArray(settings[index]) ? settings[index] : []
    return [...values]
  })

const parseCsvList = (value) => {
  if (!value || typeof value !== "string") return []
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
}

const parseConceptIdList = (value) => {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter((entry) => entry.length > 0)
  }
  return parseCsvList(String(value))
}

const createEmptyScheduleForm = () => ({
  staffId: null,
  staffName: "",
  scheduleId: null,
  conceptIds: [],
  operationSettings: createEmptyOperationSettings(),
})

const createEmptyConceptForm = () => ({
  staffId: null,
  staffName: "",
  scheduleId: null,
  selectedConceptIds: [],
  operationSettingsSnapshot: createEmptyOperationSettings(),
})

export default {
  components: {
    Update,
    Insert,
    ChangePassword,
    WeekTimeComponent,
  },

  data: () => ({
    list: [],
    selected: false,
    selectedItem: {},
    includeAdmins: false,

    dialog: {
      update: false,
      insert: false,
      changePassword: false,
      schedule: false,
      concept: false,
    },

    componentKey: 0,
    componentCreateKey: 0,
    componentChangePasswordKey: 0,

    weekDays: WEEK_DAYS,
    scheduleForm: createEmptyScheduleForm(),
    scheduleComponentKey: 0,
    scheduleLoading: false,
    scheduleSaving: false,

    conceptForm: createEmptyConceptForm(),
    conceptSearchKeyword: "",
    conceptOptions: [],
    conceptOptionsLoading: false,
    conceptDialogLoading: false,
    conceptSaving: false,
    conceptDictionary: {},
  }),

  computed: {
    selectedConceptDisplayList() {
      return this.conceptForm.selectedConceptIds.map((id) => ({
        id,
        title: this.getConceptTitleById(id),
      }))
    },
  },

  mounted() {
    this.load()
    this.loadConceptOptions()
  },

  watch: {
    selectedItem() {
      this.componentKey++
    },
    includeAdmins() {
      this.load()
    },
  },

  methods: {
    getProfileImageSrc(imagePath) {
      if (!imagePath) return "/upload/user/profile_image/default.png"
      return imagePath.includes("/") ? imagePath : `/upload/user/profile_image/${imagePath}`
    },

    getConceptThumbnailSrc(imagePath) {
      if (!imagePath) return ""
      return imagePath.includes("/") ? imagePath : `/upload/concept/thumbnail_image/${imagePath}`
    },

    // 목록
    load() {
      this.$http
        .post("/api/admin/staff/select", {
          params: {
            includeAdmins: this.includeAdmins,
          },
        })
        .then((res) => {
          this.list = Array.isArray(res.data) ? res.data : []
        })
    },

    openChangePasswordDialog() {
      if (!Object.keys(this.selectedItem).length) {
        alert("선택한 직원이 없습니다")
        return
      }
      this.componentChangePasswordKey++
      this.dialog.changePassword = true
    },

    createItem() {
      this.componentCreateKey++
      this.dialog.insert = true
    },

    // 삭제하기
    deleteItem() {
      if (!Object.keys(this.selectedItem).length) {
        alert("선택한 직원이 없습니다")
      } else {
        if (confirm("정말 해당 직원을 삭제하겠습니까?")) {
          this.$http
            .post("/api/admin/staff/delete", {
              params: {
                id: this.selectedItem.id,
              },
            })
            .then((res) => {
              if (res.data.affectedRows) {
                this.load()
                this.selected = false
                this.selectedItem = {}
              } else {
                alert("해당 직원을 삭제하는 도중 오류가 발생하였습니다.")
              }
            })
        }
      }
    },

    parseTimeCsv(value) {
      return parseCsvList(value)
    },

    getDaySchedule(item, day) {
      if (!item || !day) return []
      return this.parseTimeCsv(item[day.field])
    },

    hasScheduleSlots(item) {
      if (!item) return false
      return this.weekDays.some((day) => this.getDaySchedule(item, day).length > 0)
    },

    getConceptIds(item) {
      if (!item) return []
      return parseConceptIdList(item.scheduleConceptIds)
    },

    hasConcepts(item) {
      return this.getConceptIds(item).length > 0
    },

    getConceptDisplayList(item) {
      return this.getConceptIds(item).map((id) => ({
        id,
        title: this.getConceptTitleById(id),
      }))
    },

    getConceptTitleById(conceptId) {
      const concept = this.conceptDictionary[String(conceptId)]
      return concept ? concept.title : `ID ${conceptId}`
    },

    getStaffDisplayName(item) {
      if (!item || !item.name) return ""

      try {
        const parsed = JSON.parse(item.name)
        if (typeof parsed === "object" && parsed !== null && parsed.kr) {
          return parsed.kr
        }
        return item.name
      } catch (e) {
        // JSON이 아니면 DB값 그대로 표시
        return item.name
      }
    },

    ensureSelection(item) {
      if (!item) return
      this.selected = item
      this.selectedItem = item
    },

    resetScheduleForm() {
      this.scheduleForm = createEmptyScheduleForm()
      this.scheduleComponentKey++
    },

    resetConceptForm() {
      this.conceptForm = createEmptyConceptForm()
    },

    async fetchStaffScheduleRecord(staffId) {
      const { data } = await this.$http.get("/api/individual_operation_time/select/staff", {
        params: { userId: staffId },
      })

      if (Array.isArray(data) && data.length > 0) {
        const info = data[0]
        return {
          id: info.id,
          operationSettings: this.weekDays.map((day) => this.parseTimeCsv(info[day.key])),
          conceptIds: parseConceptIdList(info.conceptIds),
        }
      }
      return null
    },

    async openScheduleDialog(item) {
      this.ensureSelection(item)
      this.resetScheduleForm()
      if (!item) return

      this.scheduleForm.staffId = item.id
      this.scheduleForm.staffName = this.getStaffDisplayName(item)
      this.dialog.schedule = true
      this.scheduleLoading = true

      try {
        const record = await this.fetchStaffScheduleRecord(item.id)
        if (record) {
          this.scheduleForm.scheduleId = record.id
          this.scheduleForm.operationSettings = cloneOperationSettings(record.operationSettings)
          this.scheduleForm.conceptIds = [...record.conceptIds]
        } else {
          this.scheduleForm.operationSettings = createEmptyOperationSettings()
          this.scheduleForm.conceptIds = []
        }
      } catch (error) {
        console.error("staff schedule load error", error)
        alert("스케줄 정보를 불러오는 중 오류가 발생했습니다.")
      } finally {
        this.scheduleLoading = false
        this.scheduleComponentKey++
      }
    },

    setScheduleDay(index, value) {
      this.$set(this.scheduleForm.operationSettings, index, Array.isArray(value) ? value : [])
    },

    closeScheduleDialog() {
      this.dialog.schedule = false
      this.scheduleLoading = false
      this.resetScheduleForm()
    },

    buildSchedulePayloadFrom(operationSettings = []) {
      const payload = {}
      this.weekDays.forEach((day, index) => {
        const values = Array.isArray(operationSettings[index]) ? operationSettings[index] : []
        payload[day.key] = values.length ? values.join(",") : ""
      })
      return payload
    },

    serializeConceptIds(list) {
      if (!Array.isArray(list)) return null
      const normalized = list.map((value) => String(value).trim()).filter((value) => value.length > 0)
      const unique = Array.from(new Set(normalized))
      return unique.length ? unique.join(",") : null
    },

    async saveSchedule() {
      if (!this.scheduleForm.staffId) {
        alert("직원 정보가 올바르지 않습니다.")
        return
      }

      const payload = {
        userId: this.scheduleForm.staffId,
        appliesTo: "STAFF",
        conceptIds: this.serializeConceptIds(this.scheduleForm.conceptIds),
        ...this.buildSchedulePayloadFrom(this.scheduleForm.operationSettings),
      }

      this.scheduleSaving = true
      try {
        if (this.scheduleForm.scheduleId) {
          await this.$http.post("/api/individual_operation_time/update", {
            ...payload,
            id: this.scheduleForm.scheduleId,
          })
        } else {
          const res = await this.$http.post("/api/individual_operation_time/insert", payload)
          if (res && res.data && res.data.insertId) {
            this.scheduleForm.scheduleId = res.data.insertId
          }
        }

        alert("스케줄이 저장되었습니다.")
        this.dialog.schedule = false
        this.load()
      } catch (error) {
        console.error("staff schedule save error", error)
        alert("스케줄 저장 중 오류가 발생했습니다.")
      } finally {
        this.scheduleSaving = false
        if (!this.dialog.schedule) {
          this.resetScheduleForm()
        }
      }
    },

    async openConceptDialog(item) {
      this.ensureSelection(item)
      this.resetConceptForm()
      if (!item) return

      this.conceptForm.staffId = item.id
      this.conceptForm.staffName = this.getStaffDisplayName(item)
      this.dialog.concept = true
      this.conceptDialogLoading = true

      try {
        const record = await this.fetchStaffScheduleRecord(item.id)
        if (record) {
          this.conceptForm.scheduleId = record.id
          this.conceptForm.selectedConceptIds = [...record.conceptIds]
          this.conceptForm.operationSettingsSnapshot = cloneOperationSettings(record.operationSettings)
        } else {
          this.conceptForm.operationSettingsSnapshot = createEmptyOperationSettings()
        }
      } catch (error) {
        console.error("staff concept load error", error)
        alert("컨셉 정보를 불러오는 중 오류가 발생했습니다.")
      } finally {
        this.conceptDialogLoading = false
      }

      if (!this.conceptOptions.length) {
        this.loadConceptOptions(this.conceptSearchKeyword)
      }
    },

    async loadConceptOptions(keyword = "") {
      this.conceptOptionsLoading = true
      try {
        const { data } = await this.$http.post("/api/concept/select/search", {
          params: {
            keyword,
            productType: "premium",
          },
        })
        //const rows = Array.isArray(data) ? data.filter((concept) => concept.status === "노출") : []
        const rows = Array.isArray(data) ? data : []
        this.conceptOptions = rows
        rows.forEach((concept) => {
          this.$set(this.conceptDictionary, String(concept.id), concept)
        })
      } catch (error) {
        console.error("concept list load error", error)
        alert("컨셉 목록을 불러오는 중 오류가 발생했습니다.")
      } finally {
        this.conceptOptionsLoading = false
      }
    },

    searchConceptOptions() {
      this.loadConceptOptions(this.conceptSearchKeyword)
    },

    clearConceptSearch() {
      if (this.conceptSearchKeyword) {
        this.conceptSearchKeyword = ""
        this.loadConceptOptions("")
      }
    },

    toggleConceptSelection(conceptId) {
      const id = String(conceptId)
      if (!id) return

      const list = [...this.conceptForm.selectedConceptIds]
      const index = list.indexOf(id)
      if (index > -1) {
        list.splice(index, 1)
      } else {
        list.push(id)
      }
      this.conceptForm.selectedConceptIds = list
    },

    isConceptSelected(conceptId) {
      return this.conceptForm.selectedConceptIds.includes(String(conceptId))
    },

    closeConceptDialog() {
      this.dialog.concept = false
      this.conceptDialogLoading = false
      this.conceptSaving = false
      this.resetConceptForm()
    },

    async saveConcepts() {
      if (!this.conceptForm.staffId) {
        alert("직원 정보가 올바르지 않습니다.")
        return
      }

      const payload = {
        userId: this.conceptForm.staffId,
        appliesTo: "STAFF",
        conceptIds: this.serializeConceptIds(this.conceptForm.selectedConceptIds),
        ...this.buildSchedulePayloadFrom(this.conceptForm.operationSettingsSnapshot),
      }

      this.conceptSaving = true
      try {
        if (this.conceptForm.scheduleId) {
          await this.$http.post("/api/individual_operation_time/update", {
            ...payload,
            id: this.conceptForm.scheduleId,
          })
        } else {
          const res = await this.$http.post("/api/individual_operation_time/insert", payload)
          if (res && res.data && res.data.insertId) {
            this.conceptForm.scheduleId = res.data.insertId
          }
        }

        alert("컨셉이 저장되었습니다.")
        this.dialog.concept = false
        this.load()
      } catch (error) {
        console.error("staff concept save error", error)
        alert("컨셉 저장 중 오류가 발생했습니다.")
      } finally {
        this.conceptSaving = false
        if (!this.dialog.concept) {
          this.resetConceptForm()
        }
      }
    },
  },
}
</script>
<style scoped>
.schedule-cell {
  white-space: normal;
  vertical-align: top;
}

.schedule-line {
  font-size: 12px;
  line-height: 1.4;
}

.concept-cell {
  white-space: normal;
  vertical-align: top;
}

.concept-chip-wrapper {
  display: flex;
  flex-wrap: wrap;
}

.concept-card {
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.concept-card--selected {
  border-color: #1976d2 !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
}

.concept-thumb {
  background-color: #f5f5f5;
}
</style>
